import { useState } from "react";

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  benefits: string[];
}

interface JobEditModalProps {
  job: Job;
  onClose: () => void;
  onSave: (updated: Job) => void;
}

export default function JobEditModal({ job, onClose, onSave }: JobEditModalProps) {
  const [form, setForm] = useState({
    title: job.title,
    location: job.location,
    type: job.type,
    salary: job.salary,
    description: job.description,
    requirements: job.requirements.join("\n"),
    benefits: job.benefits.join("\n"),
  });
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    onSave({
      ...job,
      title: form.title,
      location: form.location,
      type: form.type,
      salary: form.salary,
      description: form.description,
      requirements: form.requirements.split("\n").filter(Boolean),
      benefits: form.benefits.split("\n").filter(Boolean),
    });
    setSaved(true);
    setTimeout(() => { setSaved(false); onClose(); }, 1200);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900">Edit Job Posting</h2>
            <p className="text-xs text-gray-400 mt-0.5">{job.company}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
            <i className="ri-close-line text-gray-600" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Job Title */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Title</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              placeholder="e.g. Warehouse Operations Manager"
            />
          </div>

          {/* Location + Type */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Location</label>
              <input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
                placeholder="e.g. Los Angeles, CA"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors cursor-pointer bg-white"
              >
                <option>Full-time</option>
                <option>Part-time</option>
                <option>Contract</option>
                <option>Internship</option>
              </select>
            </div>
          </div>

          {/* Salary */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Salary Range</label>
            <input
              value={form.salary}
              onChange={(e) => setForm({ ...form, salary: e.target.value })}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors"
              placeholder="e.g. $50,000 - $65,000/yr"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5}
              maxLength={500}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
              placeholder="Describe the role, responsibilities, and what a typical day looks like..."
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.description.length}/500</p>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Requirements <span className="font-normal text-gray-400">(one per line)</span></label>
            <textarea
              value={form.requirements}
              onChange={(e) => setForm({ ...form, requirements: e.target.value })}
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
              placeholder={"2+ years experience\nStrong communication skills\nForklift certification preferred"}
            />
          </div>

          {/* Benefits */}
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">Benefits <span className="font-normal text-gray-400">(one per line)</span></label>
            <textarea
              value={form.benefits}
              onChange={(e) => setForm({ ...form, benefits: e.target.value })}
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-400 transition-colors resize-none"
              placeholder={"Health Insurance\n401(k) with 5% match\nPaid Time Off"}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 text-gray-600 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap">
            Cancel
          </button>
          <button
            onClick={handleSave}
            className={`flex-1 py-3 text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${saved ? "bg-green-500 text-white" : "bg-orange-500 text-white hover:bg-orange-600"}`}
          >
            {saved ? <><i className="ri-check-line" /> Saved!</> : <><i className="ri-save-line" /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
}
